import { routes } from '@/shared/routes';
import type { AmlContextResponse, XamaClientSearchListItem, XamaClientSearchResponse } from '@/shared/types/aml';
import { ButtonAction } from '@/shared/types/button';
import { formatDayMonthYear } from '@/shared/utils/format';
import { utcTomorrow } from '@/shared/utils/utc';

import { execute, modelId } from '@/front/actions/utils';
import {
  AmlRiskAssessmentModalOptions,
  isAmlAlreadyCompletedToday,
  openAmlRiskAssessmentModal,
} from '@/front/amlRiskAssessmentModal';
import { GET, POST } from '@/front/fetch';
import { alertModal, confirmModal } from '@/front/modal';
import { hideOffcanvas } from '@/front/offcanvas';
import { getEditableFieldValue } from '@/front/utils/dom';

const warnIfAmlAlreadyCompletedToday = (amlLastCheckedDate?: string | null): boolean => {
  if (!isAmlAlreadyCompletedToday(amlLastCheckedDate)) return false;
  const tomorrow = utcTomorrow();
  alertModal(
    `You have already recorded an AML risk assessment for this client today. The next assessment can be recorded from ${formatDayMonthYear(tomorrow)}.`,
  );
  return true;
};

export const checkAmlIntercept = async (
  taskId: string,
  options: Pick<AmlRiskAssessmentModalOptions, 'onComplete' | 'skipRedirect'>,
): Promise<boolean> => {
  try {
    const res = await GET(routes.tasks.amlContext({ id: taskId }));
    const amlContext: AmlContextResponse = await res.json();

    if (!amlContext.requiresAml) {
      return false;
    }

    if (amlContext.xamaLinked) {
      alertModal('AML Risk Assessment is managed by the external AML provider');
      return true;
    }

    if (!amlContext.clientId) {
      console.error('Missing client data for AML task');
      return false;
    }

    if (warnIfAmlAlreadyCompletedToday(amlContext.amlLastCheckedDate)) return true;

    openAmlRiskAssessmentModal({
      clientId: amlContext.clientId,
      clientType: amlContext.clientType,
      clientName: amlContext.clientName ?? undefined,
      taskId,
      currentRiskLevel: amlContext.currentRiskLevel,
      amlLastCheckedDate: amlContext.amlLastCheckedDate ?? undefined,
      ...options,
    });

    return true;
  } catch (error) {
    console.error('Failed to check AML context', error);
    return false;
  }
};

const getClientModel = (button: HTMLElement): string =>
  button.dataset.model ||
  button.closest<HTMLElement>('[data-model]')?.dataset.model ||
  document.querySelector<HTMLElement>('[data-model]')?.dataset.model ||
  'Organisation';

const normalizeComparableName = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

const XAMA_PREFLIGHT_DUPLICATE_CONFIRM =
  'Xama returned one or more clients that may match this client. Do you still want to create a new client in Xama? This may result in duplicates.';

const executeAmlAction = async (button: HTMLAnchorElement | HTMLButtonElement, getUrl: (id: string) => string) => {
  const id = button.dataset.value ?? modelId();

  if (id) {
    const { res } = await execute(POST, getUrl(id));

    if (res.ok) {
      window.location.reload();
    }
  }
};

const executeAmlCreateWithXamaPreflight = async (
  button: HTMLAnchorElement | HTMLButtonElement,
  getPostUrl: (id: string) => string,
) => {
  const id = button.dataset.value ?? modelId();
  if (!id) return;

  const model = getClientModel(button);
  const preflight = button.dataset.xamaPreflightQuery?.trim();

  if (preflight && preflight.length >= 3) {
    try {
      const url =
        model === 'Individual'
          ? `${routes.individuals.xamaSearch({ id })}?query=${encodeURIComponent(preflight)}`
          : `${routes.organisations.xamaSearch({ id })}?query=${encodeURIComponent(preflight)}`;
      const res = await GET(url);
      const data = (await res.json()) as XamaClientSearchResponse | { message?: string };

      if (!res.ok) {
        alertModal(('message' in data && data.message) || 'Failed to search Xama clients');
        return;
      }

      const searchData = data as XamaClientSearchResponse;

      if (searchData.items.length > 0) {
        const proceed = await confirmModal(XAMA_PREFLIGHT_DUPLICATE_CONFIRM, 'Create anyway', 'warning');
        if (!proceed) return;
      }
    } catch (error) {
      console.error('Error searching Xama clients before create', error);
      alertModal('Failed to search Xama clients');
      return;
    }
  }

  const { res } = await execute(POST, getPostUrl(id));
  if (res.ok) {
    window.location.reload();
  }
};

export const runOrganisationAmlCheck = async (button: HTMLAnchorElement | HTMLButtonElement) =>
  executeAmlCreateWithXamaPreflight(button, (id) => routes.organisations.amlCheck({ id }));

export const syncOrganisationAml = async (button: HTMLAnchorElement | HTMLButtonElement) =>
  executeAmlAction(button, (id) => routes.organisations.amlSync({ id }));

export const runIndividualAmlCheck = async (button: HTMLAnchorElement | HTMLButtonElement) =>
  executeAmlCreateWithXamaPreflight(button, (id) => routes.individuals.amlCheck({ id }));

export const syncIndividualAml = async (button: HTMLAnchorElement | HTMLButtonElement) =>
  executeAmlAction(button, (id) => routes.individuals.amlSync({ id }));

export const searchXamaClients = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const model = getClientModel(button);
  const clientModelId = button.dataset.value ?? (document.getElementById('modelId') as HTMLInputElement | null)?.value;
  const searchInput = document.getElementById('xamaSearchInput') as HTMLInputElement | null;
  const resultsDiv = document.getElementById('xamaSearchResults');
  const resultsList = document.getElementById('xamaSearchResultsList');

  if (!clientModelId || !searchInput || !resultsDiv || !resultsList) {
    return;
  }

  let query = searchInput.value.trim();
  if (!query) {
    query = button.dataset.xamaDefaultQuery?.trim() ?? '';
  }

  if (query.length < 3) {
    alertModal('Please enter at least 3 characters.');
    return;
  }

  if (button instanceof HTMLButtonElement) {
    button.disabled = true;
  }

  try {
    const url =
      model === 'Individual'
        ? `${routes.individuals.xamaSearch({ id: clientModelId })}?query=${encodeURIComponent(query)}`
        : `${routes.organisations.xamaSearch({ id: clientModelId })}?query=${encodeURIComponent(query)}`;
    const res = await GET(url);
    const data = await res.json();

    if (res.ok && data.items && Array.isArray(data.items)) {
      if (data.items.length === 0) {
        resultsList.innerHTML =
          '<div class="text-muted small">No clients found. Click "Create in Xama" to create a new one.</div>';
        resultsDiv.style.display = 'block';
      } else {
        const items = data.items as XamaClientSearchListItem[];
        resultsList.innerHTML = items
          .map(
            (item, index) => `
          <div class="d-flex justify-content-between align-items-center p-2${index < items.length - 1 ? ' border-bottom' : ''}">
            <div>
              <strong>${item.name || 'Unnamed'}${item.email ? ` <span class="text-muted">(${item.email})</span>` : ''}</strong>
              ${item.type ? `<span class="badge bg-secondary ms-2">${item.type}</span>` : ''}
              ${item.status ? `<span class="badge bg-${item.status === 'ACTIVE' ? 'success' : 'secondary'} ms-2">${item.status}</span>` : ''}
            </div>
            <button
              class="btn btn-sm btn-primary"
              data-action="linkXamaClient"
              data-value="${clientModelId}"
              data-model="${model}"
              data-xama-account-id="${item.id}"
            >
              <i class="fa-solid fa-link me-1"></i>Link
            </button>
          </div>
        `,
          )
          .join('');
        resultsDiv.style.display = 'block';

        [...resultsList.querySelectorAll('a[data-action], button[data-action]')]
          .map((btn) => btn as HTMLAnchorElement | HTMLButtonElement)
          .forEach((btn) => {
            const newBtn = btn.cloneNode(true) as HTMLAnchorElement | HTMLButtonElement;
            btn.parentNode?.replaceChild(newBtn, btn);

            newBtn.addEventListener('click', async (event) => {
              event.preventDefault();
              const action = newBtn.dataset.action as ButtonAction;
              if (action === 'linkXamaClient') {
                await linkXamaClient(newBtn);
              }
            });
          });
      }
    } else {
      alertModal(data.message || 'Failed to search Xama clients');
    }
  } catch (error) {
    console.error('Error searching Xama clients', error);
    alertModal('Failed to search Xama clients');
  } finally {
    if (button instanceof HTMLButtonElement) {
      button.disabled = false;
    }
  }
};

export const linkXamaClient = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const model = getClientModel(button);
  const modelIdValue = button.dataset.value ?? modelId();
  const xamaAccountId = button.dataset.xamaAccountId;

  if (!modelIdValue || !xamaAccountId) {
    return;
  }

  if (button instanceof HTMLButtonElement) {
    button.disabled = true;
  }

  try {
    if (model === 'Organisation') {
      const organisationRegNumber = getEditableFieldValue('registrationNumber');
      const organisationName = getEditableFieldValue('name').trim();

      if (organisationRegNumber || organisationName) {
        const xamaAccountUrl = routes.organisations.xamaAccount({ id: modelIdValue, xamaAccountId });
        const xamaAccountRes = await GET(xamaAccountUrl);

        if (xamaAccountRes.ok) {
          const xamaAccount = await xamaAccountRes.json();
          const xamaRegNumber = (xamaAccount.reg_number || '').trim();
          const xamaName = (xamaAccount.name || '').trim();

          if (organisationRegNumber) {
            if (xamaRegNumber && organisationRegNumber.toLowerCase() !== xamaRegNumber.toLowerCase()) {
              const confirmed = await confirmModal(
                `Registration numbers do not match.\n\nPracticeFlow: "${organisationRegNumber}"\nXama: "${xamaRegNumber}"\n\nAre you sure you want to link these?`,
                'Link',
                'warning',
              );

              if (!confirmed) {
                return;
              }
            }
          } else if (organisationName) {
            if (xamaName && normalizeComparableName(organisationName) !== normalizeComparableName(xamaName)) {
              const confirmed = await confirmModal(
                `Names do not match.\n\nPracticeFlow: "${organisationName}"\nXama: "${xamaName}"\n\nAre you sure you want to link these?`,
                'Link',
                'warning',
              );

              if (!confirmed) {
                return;
              }
            }
          }
        }
      }
    } else if (model === 'Individual') {
      const given = getEditableFieldValue('givenName');
      const surname = getEditableFieldValue('surname');
      const individualName = [given, surname].filter(Boolean).join(' ').trim();

      if (individualName) {
        const xamaAccountUrl = routes.individuals.xamaAccount({ id: modelIdValue, xamaAccountId });
        const xamaAccountRes = await GET(xamaAccountUrl);

        if (xamaAccountRes.ok) {
          const xamaAccount = await xamaAccountRes.json();
          const xamaName = (xamaAccount.name || '').trim();

          if (xamaName && normalizeComparableName(individualName) !== normalizeComparableName(xamaName)) {
            const confirmed = await confirmModal(
              `Names do not match.\n\nPracticeFlow: "${individualName}"\nXama: "${xamaName}"\n\nAre you sure you want to link these?`,
              'Link',
              'warning',
            );

            if (!confirmed) {
              return;
            }
          }
        }
      }
    }

    const linkUrl =
      model === 'Individual'
        ? routes.individuals.xamaLink({ id: modelIdValue })
        : routes.organisations.xamaLink({ id: modelIdValue });

    const { res } = await execute(POST, linkUrl, {
      xamaAccountId,
    });

    if (res.ok) {
      window.location.reload();
    } else {
      const data = await res.json();
      alertModal(data.message || 'Failed to link client to Xama');
    }
  } catch (error) {
    console.error('Error linking Xama client', error);
    alertModal('Failed to link client to Xama');
  } finally {
    if (button instanceof HTMLButtonElement) {
      button.disabled = false;
    }
  }
};

export const unlinkXamaClient = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const model = getClientModel(button);
  const modelIdValue = button.dataset.value ?? modelId();

  if (!modelIdValue) {
    return;
  }

  if (button instanceof HTMLButtonElement) {
    button.disabled = true;
  }

  try {
    const unlinkUrl =
      model === 'Individual'
        ? routes.individuals.xamaUnlink({ id: modelIdValue })
        : routes.organisations.xamaUnlink({ id: modelIdValue });

    const { res } = await execute(POST, unlinkUrl);

    if (res.ok) {
      window.location.reload();
    } else {
      const data = await res.json();
      alertModal(data.message || 'Failed to unlink client from Xama');
    }
  } catch (error) {
    console.error('Error unlinking Xama client', error);
    alertModal('Failed to unlink client from Xama');
  } finally {
    if (button instanceof HTMLButtonElement) {
      button.disabled = false;
    }
  }
};

export const openAmlRiskAssessmentModalAction = async (button: HTMLAnchorElement | HTMLButtonElement) => {
  const taskId = button.dataset.value;

  if (!taskId) {
    alertModal('Missing task information. Please save the task first.');
    return;
  }

  try {
    const res = await GET(routes.tasks.amlContext({ id: taskId }));

    if (!res.ok) {
      alertModal('Failed to load AML information. Please try again.');
      return;
    }

    const amlContext: AmlContextResponse = await res.json();

    if (!amlContext.requiresAml || !amlContext.clientId) {
      alertModal('Missing client information. Please save the task first.');
      return;
    }

    if (warnIfAmlAlreadyCompletedToday(amlContext.amlLastCheckedDate)) return;

    openAmlRiskAssessmentModal({
      clientId: amlContext.clientId,
      clientType: amlContext.clientType,
      clientName: amlContext.clientName ?? undefined,
      taskId,
      currentRiskLevel: amlContext.currentRiskLevel,
      amlLastCheckedDate: amlContext.amlLastCheckedDate ?? undefined,
      onComplete: () => {
        const offcanvas = document.querySelector('.offcanvas');
        if (offcanvas) {
          hideOffcanvas({ refreshGrids: true });
        } else {
          window.location.reload();
        }
      },
    });
  } catch (error) {
    console.error('Failed to fetch AML context', error);
    alertModal('Failed to load AML information. Please try again.');
  }
};
